import { warningMessageForRequiredField_0 } from '../strings.locstring.json';
import loc from 'owa-localize';
import { observer } from 'mobx-react';
/* tslint:disable:jsx-no-lambda WI:47648 */
import { MAX_TITLE_LENGTH } from '../helpers/EditableCardConsts';
import isInputTextValid from '../utils/view/isInputTextValid';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import findLocation from 'owa-location-picker/lib/actions/findLocation';
import { generateLocationWellColor } from 'owa-location-common-well/lib/utils/generateLocationWellStyle';
import { LocationPersonaControlViewState } from 'owa-location-persona';
import FindLocationResult from 'owa-location-picker/lib/components/FindLocationResult';
import FindLocationResultState from 'owa-location-picker/lib/data/schema/FindLocationResultState';

import * as React from 'react';
import PostalAddress from 'txp-data/lib/schema/innerSchema/PostalAddress';
import { CardMode } from 'txp-data/lib/schema/viewSchema/CardMode';
import TxpDataRow from 'txp-data/lib/schema/viewSchema/TxpDataRow';
import setCursorPositionToStart from '../utils/view/setCursorPositionToStart';
import addHighlightedFindLocations from 'owa-location-picker/lib/utils/addHighlightedFindLocations';
import shiftFindResultsHighlightIndex from 'owa-location-picker/lib/utils/shiftFindResultsHighlightIndex';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { lazyCreateTxpPostalAddressFromLocationPersona } from 'owa-location-persona';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';

let styles = require('./Txp.scss');

export interface EditableLocationPickerState {
    findLocationResultState: FindLocationResultState;
}

export interface EditableLocationPickerProps {
    cardMode: CardMode;
    editableText: string;
    editableData: TxpDataRow;
    location: PostalAddress;
    onLocationChanged?: (location: PostalAddress, stringAddress: string) => void;
    getDisplayAddress?: (location: PostalAddress) => string;
}

@observer
export default class EditableLocationPicker extends React.Component<
    EditableLocationPickerProps,
    EditableLocationPickerState
> {
    private locationInputContainer: HTMLElement;

    constructor(props: EditableLocationPickerProps) {
        super(props);

        this.state = {
            findLocationResultState: {
                queryString: '',
                shouldShowFindResult: true,
                findResultSet: [],
                findResultHightlightIndex: 0,
                isSearching: false,
                addLocationHandler: this.locationChanged,
                currentDevicePosition: null,
            },
        };
    }

    render() {
        const { cardMode, editableData, editableText } = this.props;
        let { placeholder, isRequired } = editableData;

        return (
            <>
                <div
                    ref={ref => {
                        this.locationInputContainer = ref;
                    }}>
                    <TextField
                        required={isRequired}
                        onChange={this.textChanged}
                        borderless={true}
                        value={editableText}
                        onFocus={this.onFocus}
                        placeholder={placeholder}
                        className={styles.editableTextField}
                        underlined={true}
                        maxLength={MAX_TITLE_LENGTH}
                        readOnly={cardMode == CardMode.Process}
                        onKeyDown={this.onKeyDown}
                        onGetErrorMessage={this.validateText}
                        componentRef={
                            cardMode === CardMode.Process ? setCursorPositionToStart : null
                        }
                    />
                </div>
                <FindLocationResult
                    locationWellColor={generateLocationWellColor(null /* colorScheme */)}
                    findLocationResultState={this.state.findLocationResultState}
                    queryInputGetter={() => {
                        return this.locationInputContainer;
                    }}
                    showAddRoom={false}
                    roomFreeBusyEnabled={false}
                    dismissOnEmptyQuery={true}
                />
            </>
        );
    }

    private onKeyDown = (target: React.KeyboardEvent<unknown>) => {
        if (this.state.findLocationResultState.shouldShowFindResult) {
            switch (target.keyCode) {
                case KeyCodes.down:
                    this.shiftFindResultHighlightIndex(
                        false /**disableUseThisLocation */,
                        true /**isIncrement */
                    );
                    return;
                case KeyCodes.up:
                    this.shiftFindResultHighlightIndex(
                        false /**disableUseThisLocation */,
                        false /**isIncrement */
                    );
                    return;
                case KeyCodes.enter:
                case KeyCodes.tab:
                    addHighlightedFindLocations(
                        false /**disableUseThisLocation */,
                        this.state.findLocationResultState
                    );
                    return;
                default:
                    return;
            }
        }
    };

    private shiftFindResultHighlightIndex = (
        disableUseThisLocation: boolean,
        isIncrement: boolean
    ) => {
        let { findLocationResultState } = this.state;

        this.setState({
            findLocationResultState: {
                ...findLocationResultState,
                findResultHightlightIndex: shiftFindResultsHighlightIndex(
                    disableUseThisLocation,
                    isIncrement,
                    findLocationResultState
                ),
            },
        });
    };

    private validateText = (value: string): string => {
        if (!isInputTextValid(value, this.props.editableData.isRequired)) {
            return loc(warningMessageForRequiredField_0);
        }
        return '';
    };

    private textChanged = async (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        text: string
    ) => {
        let lazyTxpLocation = await lazyCreateTxpPostalAddressFromLocationPersona.import();
        let txpLocation: PostalAddress = lazyTxpLocation(text);
        this.props.onLocationChanged(txpLocation, text);
        this.searchLocation(text);
    };

    private locationChanged = async (newLocation: LocationPersonaControlViewState | string) => {
        let lazyTxpLocation = await lazyCreateTxpPostalAddressFromLocationPersona.import();
        let txpLocation: PostalAddress = lazyTxpLocation(newLocation);
        let displayAddress =
            typeof newLocation === 'string'
                ? newLocation
                : this.props.getDisplayAddress(txpLocation);
        txpLocation.displayAddress = displayAddress;
        this.props.onLocationChanged(txpLocation, displayAddress);

        this.setState({
            findLocationResultState: {
                ...this.state.findLocationResultState,
                shouldShowFindResult: false,
            },
        });
    };

    private onFocus = async () => {
        if (this.props.cardMode != CardMode.Process) {
            this.searchLocation(this.props.editableText);
        }
    };

    private searchLocation = async (newLocation: string) => {
        this.setState({
            findLocationResultState: {
                ...this.state.findLocationResultState,
                queryString: newLocation || '',
                isSearching: true,
            },
        });

        const userConfig = getUserConfiguration();
        let locationResults: LocationPersonaControlViewState[] = await findLocation(
            userConfig.SessionSettings.UserEmailAddress,
            this.state.findLocationResultState,
            newLocation,
            null /* requiredAttendees */,
            null /* optionalAttendees */
        );

        this.setState({
            findLocationResultState: {
                ...this.state.findLocationResultState,
                shouldShowFindResult: true,
                findResultSet: locationResults,
                isSearching: false,
            },
        });
    };
}
